import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC, memo, MouseEvent, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { gettext, pgettext } from 'medialoopster/Internationalization';
import { ImageWithLoadingPlaceholder } from 'medialoopster/components';
import { Audio, ErrorML, AudioPreview, NoAudioPreview, Pause } from 'medialoopster/icons';
import { getResourceURI } from 'medialoopster/rest';

import {
  ERROR,
  GENERATING,
  mapStatus,
  PREPARING_IMPORT,
  PREVIEW_AVAILABLE,
  RECORDING,
} from '../../../../businessRules/models/AssetStatus';
import { AudioAsset as AudioAssetType } from '../../../../state/modules/audio/types';
import { favoritesSelectors } from '../../../../state/modules/favorites';
import { searchActions } from '../../../../state/modules/search';
import Duration from '../../../../ui/components/Duration';
import FormattedDate from '../../../../ui/components/FormattedDate';
import useAssetDragRef from '../../../../ui/hooks/useAssetDragRef';
import AssetHeader from '../AssetHeader';
import LabeledInfo from '../LabeledInfo';
import MediaAssetStatusBar from '../MediaAssetStatusBar';

const statusTitleMap: Record<string, string> = {
  [ERROR]: gettext('An error occurred'),
  [PREPARING_IMPORT]: `${gettext('Preparing import')} …`,
  [GENERATING]: gettext('Generating audio proxy'),
  [RECORDING]: gettext('Generating audio proxy'),
  [PREVIEW_AVAILABLE]: gettext('Audio preview available'),
};

const statusLevelMap: Record<string, string> = {
  [ERROR]: 'error',
  [PREPARING_IMPORT]: 'warning',
  [GENERATING]: 'default',
  [RECORDING]: 'default',
  [PREVIEW_AVAILABLE]: 'default',
};

const findBestTitle = (asset: AudioAssetType): string =>
  asset.title && asset.artist ? asset.title : asset.name;
const findArtist = (asset: AudioAssetType): string | JSX.Element =>
  asset.artist ? asset.artist : <em>{gettext('Unknown Artist')}</em>;

export interface AudioAssetProps {
  readonly asset: AudioAssetType;
  readonly isWide: boolean;
  readonly current: boolean;
  readonly inSelection: boolean;
}

const useStyles = makeStyles((theme) => ({
  cursorGrab: {
    cursor: 'grab',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
  },
  body: { display: 'flex', flexGrow: 1, alignItems: 'flex-end', paddingBottom: theme.spacing(1) },
  bodyInfo: { flexGrow: 1 },
  bodyInfoCentered: { display: 'flex', alignItems: 'center' },
  bodyInfoTitle: {
    display: 'flex',
    '&>div': {
      paddingRight: theme.spacing(4),
    },
  },
  bodyImage: { display: 'flex', paddingRight: theme.spacing(1) },
  bodyIcon: { display: 'flex', paddingRight: theme.spacing(1) },
  bodyRowCentered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}));

const renderStatusIcon = (internalStatus: string) => {
  // 'Preview' icons must be aligned with the other icons, since it has no left-right margin
  if (internalStatus === PREVIEW_AVAILABLE)
    return <AudioPreview style={{ fontSize: '1.2rem', marginLeft: '0.125rem' }} />;
  if (internalStatus === ERROR) return <ErrorML style={{ fontSize: '1.2rem' }} />;
  if (internalStatus === PREPARING_IMPORT) return <Pause style={{ fontSize: '1.2rem' }} />;
  if (internalStatus === GENERATING)
    return <NoAudioPreview style={{ fontSize: '1.2rem', marginLeft: '0.125rem' }} />;
  if (internalStatus === RECORDING)
    return <NoAudioPreview style={{ fontSize: '1.2rem', marginLeft: '0.125rem' }} />;
  return <></>;
};

const renderAssetIcon = () => <Audio fontSize="small" />;

const AudioAsset: FC<AudioAssetProps> = ({
  asset,
  isWide,
  current,
  inSelection,
}: AudioAssetProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isFavoriteListActive = useSelector(favoritesSelectors.isActive);
  const internalStatus = mapStatus(asset.status_asset);
  const imageDragRef = useAssetDragRef(asset, 'audioasset');
  const titleDragRef = useAssetDragRef(asset, 'audioasset');
  const renderAssetStatusIcon = useCallback(
    () => renderStatusIcon(internalStatus),
    [internalStatus],
  );
  const handleAssetClick = useCallback(
    (event: MouseEvent<Element>) => {
      dispatch(searchActions.clickEntry(asset.id, 'audioasset', getResourceURI(asset), event));
    },
    [dispatch, asset],
  );

  const AudioAssetStatusBar: FC = () => {
    return (
      <MediaAssetStatusBar
        asset={asset}
        isWide={isWide}
        renderAssetStatusIcon={renderAssetStatusIcon}
        statusTitle={statusTitleMap[internalStatus]}
        statusLevel={statusLevelMap[internalStatus]}
        showLabelAssetStatus={isWide || internalStatus === ERROR}
      />
    );
  };

  return (
    <AssetHeader
      isWide={isWide}
      isCurrent={current}
      inSelection={inSelection}
      onAssetClick={handleAssetClick}
    >
      {!isWide && (
        <div className={classes.header}>
          <span
            ref={titleDragRef}
            data-testid="draggableTitle"
            className={clsx(classes.bodyIcon, { [classes.cursorGrab]: isFavoriteListActive })}
          >
            {renderAssetIcon()}
          </span>
          <LabeledInfo isWide={false} label={gettext('Title')} big shorten>
            {findBestTitle(asset)}
          </LabeledInfo>
        </div>
      )}
      <div className={classes.body}>
        <div className={classes.bodyImage}>
          <ImageWithLoadingPlaceholder
            dragRef={imageDragRef}
            className={clsx({
              [classes.cursorGrab]: isFavoriteListActive,
            })}
            placeholderWidth={102}
            placeholderHeight={57}
            url={asset.thumbnail_url}
          />
        </div>

        {isWide && (
          <div className={classes.bodyInfo}>
            <div className={classes.bodyRowCentered}>
              <div className={classes.bodyInfoTitle}>
                <LabeledInfo isWide={isWide} label={gettext('Title')} big shorten>
                  {findBestTitle(asset)}
                </LabeledInfo>
                <LabeledInfo isWide={isWide} label={gettext('Artist')} big={isWide} shorten>
                  {findArtist(asset)}
                </LabeledInfo>
              </div>
              <div className={classes.bodyInfoCentered}>
                <span className={classes.bodyIcon}>{renderAssetIcon()}</span>
                {gettext('Audioasset')}
              </div>
            </div>

            <div className={classes.bodyRowCentered}>
              <LabeledInfo isWide={isWide} label={gettext('Production')}>
                {asset.production_name}
              </LabeledInfo>
              <LabeledInfo isWide={isWide} label={gettext('Duration')}>
                <Duration seconds={asset.duration} />
              </LabeledInfo>
            </div>

            <div className={classes.bodyRowCentered}>
              <AudioAssetStatusBar />
              <LabeledInfo isWide={isWide} label={gettext('Added')}>
                <FormattedDate
                  date={asset.date_add}
                  format={pgettext(
                    'Localized numeric date and time with seconds',
                    'MM/dd/yyyy h:mm:ss a',
                  )}
                />
              </LabeledInfo>
            </div>
          </div>
        )}

        {!isWide && (
          <div className={classes.bodyInfo}>
            <div className={classes.bodyRowCentered}>
              <LabeledInfo isWide={isWide} label={gettext('Artist')} big={isWide} shorten>
                {findArtist(asset)}
              </LabeledInfo>
            </div>

            <div className={classes.bodyRowCentered}>
              <LabeledInfo isWide={isWide} label={gettext('Duration')}>
                <Duration seconds={asset.duration} />
              </LabeledInfo>
              <LabeledInfo isWide={isWide} label={gettext('Date added')}>
                <FormattedDate
                  date={asset.date_add}
                  format={pgettext('Localized numeric date', 'MM/dd/yyyy')}
                />
              </LabeledInfo>
            </div>

            <div className={classes.bodyRowCentered}>
              <LabeledInfo isWide={isWide} label={gettext('Production')}>
                {asset.production_name}
              </LabeledInfo>
              <LabeledInfo isWide={isWide} label={gettext('Time added')}>
                <FormattedDate
                  date={asset.date_add}
                  format={pgettext('Localized time with seconds', 'h:mm:ss a')}
                />
              </LabeledInfo>
            </div>

            <div className={classes.bodyRowCentered}>
              <AudioAssetStatusBar />
            </div>
          </div>
        )}
      </div>
    </AssetHeader>
  );
};

export default memo(AudioAsset);
